import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal, Button, Form, Alert, Spinner, Badge, Row, Col, ListGroup } from 'react-bootstrap';
import { apiRequest } from '../utils/api';

type AdminModerationStatus = 'OPEN' | 'REVIEWED' | 'FLAGGED' | 'RESOLVED';

interface AdminModerationParticipant {
    userId: string;
    email: string | null;
    isAdmin: boolean;
    avatar?: string | null;
}

interface AdminModerationConversationListItem {
    conversationId: string;
    participants: AdminModerationParticipant[];
    buildingId?: string;
    buildingName?: string;
    buildingAddress?: string;
    listingContext?: {
        offMarketSellWillingnessId?: string | null;
        offMarketBuyWillingnessId?: string | null;
    };
    lastMessagePreview?: string;
    lastMessageAt?: string | null;
    moderationStatus: AdminModerationStatus;
    moderationUpdatedAt?: string | null;
    isEnded?: boolean;
}

interface AdminModerationMessageAttachment {
    id?: string;
    type?: string;
    fileName?: string;
    mimeType?: string;
    sizeBytes?: number;
}

interface AdminModerationMessage {
    messageId: string;
    senderId: string | null;
    senderEmail: string | null;
    text: string;
    timestamp: string | null;
    attachments: AdminModerationMessageAttachment[];
    moderation: {
        hidden: boolean;
        hiddenAt?: string | null;
        hiddenByAdminUserId?: string | null;
        reason?: string | null;
    };
}

interface AdminModerationActionLog {
    id: string;
    action?: string;
    reason?: string | null;
    performedByAdminUserId?: string | null;
    performedAt?: string | null;
    messageId?: string | null;
    metadata?: any;
}

interface AdminModerationConversationDetail {
    conversationId: string;
    buildingId?: string;
    buildingName?: string;
    buildingAddress?: string;
    listingContext?: {
        offMarketSellWillingnessId?: string | null;
        offMarketBuyWillingnessId?: string | null;
    };
    participants: AdminModerationParticipant[];
    isEnded: boolean;
    endedAt?: string | null;
    moderationCase?: {
        status?: AdminModerationStatus | string;
        lastModerationActionAt?: string | null;
    };
    messages: AdminModerationMessage[];
    actionLogs: AdminModerationActionLog[];
}

interface AdminModerationPagination {
    page: number;
    limit: number;
    total: number;
    has_more: boolean;
}

interface AdminModerationToolProps {
    isLoggedIn: boolean;
    isAdmin: boolean;
    isMobile: boolean;
    adminModerationVisible: boolean;
    setAdminModerationVisible: (visible: boolean) => void;
    adminModerationError: string | null;
    setAdminModerationError: (error: string | null) => void;
    adminModerationSuccess: string | null;
    setAdminModerationSuccess: (success: string | null) => void;
    hasCheckedLogin: boolean;
}

const AdminModerationTool = (props: AdminModerationToolProps) => {
    const { 
        isLoggedIn,
        isAdmin,
        isMobile,
        adminModerationVisible,
        setAdminModerationVisible,
        adminModerationError,
        setAdminModerationError,
        adminModerationSuccess,
        setAdminModerationSuccess,
        hasCheckedLogin,
    } = props;
    const history = useHistory();
    const location = useLocation();

    // --- Admin: conversation moderation (lightweight MVP) ---
    const [adminModerationLoading, setAdminModerationLoading] = useState(false);
    const [adminModerationDetailLoading, setAdminModerationDetailLoading] = useState(false);
    const [adminModerationStatus, setAdminModerationStatus] = useState<AdminModerationStatus | ''>('OPEN');
    const [adminModerationSearchInput, setAdminModerationSearchInput] = useState('');
    const [adminModerationSearch, setAdminModerationSearch] = useState('');
    const [adminModerationConversations, setAdminModerationConversations] = useState<AdminModerationConversationListItem[]>([]);
    const [adminModerationPagination, setAdminModerationPagination] = useState<AdminModerationPagination | null>(null);
    const [adminModerationSelectedConversationId, setAdminModerationSelectedConversationId] = useState<string | null>(null);
    const [adminModerationDetail, setAdminModerationDetail] = useState<AdminModerationConversationDetail | null>(null);
    const [reasonModal, setReasonModal] = useState<{ type: 'FLAG_CONVERSATION' | 'HIDE_MESSAGE'; targetId: string } | null>(null);
    const [reasonText, setReasonText] = useState('');
    const [reasonSubmitting, setReasonSubmitting] = useState(false);

    const getAdminModerationStatusVariant = useCallback((status: string) => {
        switch ((status || '').toUpperCase()) {
            case 'FLAGGED':
                return 'danger';
            case 'REVIEWED':
                return 'success';
            case 'RESOLVED':
                return 'secondary';
            case 'OPEN':
            default:
                return 'info';
        }
    }, []);

    const adminParticipantsLabel = useCallback((participants: AdminModerationParticipant[]) => {
        const parts = (participants || [])
            .map((p) => p.email || (p.userId ? p.userId.slice(-6) : 'Unknown'))
            .filter(Boolean);
        return parts.join(' • ');
    }, []);

    const closeAdminModeration = useCallback(() => {
        setAdminModerationVisible(false);
        setAdminModerationError(null);
        setAdminModerationSuccess(null);
        setReasonModal(null);
        setReasonText('');
        setReasonSubmitting(false);

        // If opened via deep link, clean it up
        const url = new URL(window.location.href);
        if (url.searchParams.get('admin-moderation')) {
            url.searchParams.delete('admin-moderation');
            history.replace(url.pathname + url.search);
        }
    }, [history]);

    const fetchAdminModerationInbox = useCallback(async (opts: { page?: number; append?: boolean } = {}) => {
        if (!isLoggedIn || !isAdmin) return [] as AdminModerationConversationListItem[];
        const page = opts.page ?? 1;
        const append = opts.append ?? false;

        try {
            setAdminModerationLoading(true);
            setAdminModerationError(null);

            const params = new URLSearchParams();
            if (adminModerationStatus) params.set('status', adminModerationStatus);
            if (adminModerationSearch) params.set('search', adminModerationSearch);
            params.set('page', String(page));
            params.set('limit', '50');

            const response = await apiRequest(`/api/admin/moderation/conversations?${params.toString()}`, {
                method: 'GET',
            });

            const items = (response?.items || []) as AdminModerationConversationListItem[];
            const pagination = response?.pagination as AdminModerationPagination | undefined;

            setAdminModerationConversations((prev) => (append ? [...prev, ...items] : items));
            setAdminModerationPagination(pagination || null);
            return items;
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Failed to load moderation inbox');
            return [] as AdminModerationConversationListItem[];
        } finally {
            setAdminModerationLoading(false);
        }
    }, [isLoggedIn, isAdmin, adminModerationStatus, adminModerationSearch]);

    const fetchAdminModerationConversationDetail = useCallback(async (conversationId: string) => {
        if (!isLoggedIn || !isAdmin) return;
        try {
            setAdminModerationDetailLoading(true);
            setAdminModerationError(null);

            const detail = await apiRequest(`/api/admin/moderation/conversations/${conversationId}`, {
                method: 'GET',
            });

            setAdminModerationDetail(detail as AdminModerationConversationDetail);
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Failed to load conversation detail');
            setAdminModerationDetail(null);
        } finally {
            setAdminModerationDetailLoading(false);
        }
    }, [isLoggedIn, isAdmin]);

    const refreshAdminModeration = useCallback(async () => {
        const refreshedInboxItems = await fetchAdminModerationInbox({ page: 1, append: false });
        if (!adminModerationSelectedConversationId) return;

        const selectedStillExists = (refreshedInboxItems || []).some(
            (conversation) => conversation.conversationId === adminModerationSelectedConversationId,
        );

        if (!selectedStillExists) {
            setAdminModerationSelectedConversationId(null);
            setAdminModerationDetail(null);
            return;
        }

        if (adminModerationSelectedConversationId) {
            await fetchAdminModerationConversationDetail(adminModerationSelectedConversationId);
        }
    }, [adminModerationSelectedConversationId, fetchAdminModerationInbox, fetchAdminModerationConversationDetail]);

    const handleAdminModerationLoadMore = useCallback(async () => {
        if (!adminModerationPagination?.has_more) return;
        const nextPage = (adminModerationPagination.page || 1) + 1;
        await fetchAdminModerationInbox({ page: nextPage, append: true });
    }, [adminModerationPagination, fetchAdminModerationInbox]);

    const handleAdminMarkReviewed = useCallback(async () => {
        if (!adminModerationSelectedConversationId) return;
        try {
            setAdminModerationDetailLoading(true);
            setAdminModerationError(null);
            setAdminModerationSuccess(null);

            await apiRequest(
                `/api/admin/moderation/conversations/${adminModerationSelectedConversationId}/actions/mark-reviewed`,
                { method: 'POST', body: {} },
            );

            setAdminModerationSuccess('Marked as reviewed.');
            await refreshAdminModeration();
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Failed to mark as reviewed');
        } finally {
            setAdminModerationDetailLoading(false);
        }
    }, [adminModerationSelectedConversationId, refreshAdminModeration]);

    const handleAdminResolve = useCallback(async () => {
        if (!adminModerationSelectedConversationId) return;
        try {
            setAdminModerationDetailLoading(true);
            setAdminModerationError(null);
            setAdminModerationSuccess(null);

            await apiRequest(
                `/api/admin/moderation/conversations/${adminModerationSelectedConversationId}/actions/resolve`,
                { method: 'POST', body: {} },
            );

            setAdminModerationSuccess('Resolved.');
            await refreshAdminModeration();
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Failed to resolve');
        } finally {
            setAdminModerationDetailLoading(false);
        }
    }, [adminModerationSelectedConversationId, refreshAdminModeration]);

    const handleAdminUnhideMessage = useCallback(async (messageId: string) => {
        try {
            setAdminModerationDetailLoading(true);
            setAdminModerationError(null);
            setAdminModerationSuccess(null);

            await apiRequest(`/api/admin/moderation/messages/${messageId}/actions/unhide`, {
                method: 'POST',
                body: {},
            });

            setAdminModerationSuccess('Message unhidden.');
            await refreshAdminModeration();
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Failed to unhide message');
        } finally {
            setAdminModerationDetailLoading(false);
        }
    }, [refreshAdminModeration]);

    const openFlagReasonModal = useCallback(() => {
        if (!adminModerationSelectedConversationId) return;
        setReasonModal({ type: 'FLAG_CONVERSATION', targetId: adminModerationSelectedConversationId });
        setReasonText('');
        setAdminModerationError(null);
        setAdminModerationSuccess(null);
    }, [adminModerationSelectedConversationId]);

    const openHideReasonModal = useCallback((messageId: string) => {
        setReasonModal({ type: 'HIDE_MESSAGE', targetId: messageId });
        setReasonText('');
        setAdminModerationError(null);
        setAdminModerationSuccess(null);
    }, []);

    const handleReasonSubmit = useCallback(async () => {
        if (!reasonModal) return;
        const trimmed = reasonText.trim();
        if (!trimmed) {
            setAdminModerationError('Reason is required.');
            return;
        }

        try {
            setReasonSubmitting(true);
            setAdminModerationError(null);
            setAdminModerationSuccess(null);

            if (reasonModal.type === 'FLAG_CONVERSATION') {
                await apiRequest(`/api/admin/moderation/conversations/${reasonModal.targetId}/actions/flag`, {
                    method: 'POST',
                    body: { reason: trimmed },
                });
                setAdminModerationSuccess('Conversation flagged.');
            } else if (reasonModal.type === 'HIDE_MESSAGE') {
                await apiRequest(`/api/admin/moderation/messages/${reasonModal.targetId}/actions/hide`, {
                    method: 'POST',
                    body: { reason: trimmed },
                });
                setAdminModerationSuccess('Message hidden.');
            }

            setReasonModal(null);
            setReasonText('');
            await refreshAdminModeration();
        } catch (err: any) {
            setAdminModerationError(err?.error || err?.message || 'Moderation action failed');
        } finally {
            setReasonSubmitting(false);
        }
    }, [reasonModal, reasonText, refreshAdminModeration]);

    // Deep link: ?admin-moderation=1
    useEffect(() => {
        if (!hasCheckedLogin) return;
        const params = new URLSearchParams(location.search);
        const shouldOpen = params.get('admin-moderation') === '1';
        if (shouldOpen && isLoggedIn && isAdmin && !isMobile) {
            setAdminModerationVisible(true);
        }
    }, [location.search, hasCheckedLogin, isLoggedIn, isAdmin, isMobile]);

    // Force-close moderation UI if auth/role/device guard fails while open
    useEffect(() => {
        if (!adminModerationVisible) return;
        if (!isLoggedIn || !isAdmin || isMobile) {
            closeAdminModeration();
        }
    }, [adminModerationVisible, isLoggedIn, isAdmin, isMobile, closeAdminModeration]);

    // Load inbox when opened or filters applied
    useEffect(() => {
        if (!adminModerationVisible) return;
        setAdminModerationSelectedConversationId(null);
        setAdminModerationDetail(null);
        fetchAdminModerationInbox({ page: 1, append: false });
    }, [adminModerationVisible, adminModerationStatus, adminModerationSearch, fetchAdminModerationInbox]);

    // Load detail when selecting a conversation
    useEffect(() => {
        if (!adminModerationVisible) return;
        if (!adminModerationSelectedConversationId) return;
        fetchAdminModerationConversationDetail(adminModerationSelectedConversationId);
    }, [adminModerationVisible, adminModerationSelectedConversationId, fetchAdminModerationConversationDetail]);

    return (
        <>
            <Modal show={adminModerationVisible && isLoggedIn && isAdmin && !isMobile} onHide={closeAdminModeration} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Admin: Conversation moderation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {adminModerationError && (
                        <Alert variant="danger" dismissible onClose={() => setAdminModerationError(null)}>
                            {adminModerationError}
                        </Alert>
                    )}
                    {adminModerationSuccess && (
                        <Alert variant="success" dismissible onClose={() => setAdminModerationSuccess(null)}>
                            {adminModerationSuccess}
                        </Alert>
                    )}

                    <Row>
                        <Col md={4} style={{ maxHeight: '72vh', overflowY: 'auto', borderRight: '1px solid #eee' }}>
                            <Form className="mb-3">
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={adminModerationStatus}
                                        onChange={(e) => setAdminModerationStatus((e.target.value || '') as any)}
                                        disabled={adminModerationLoading}
                                    >
                                        <option value="">All</option>
                                        <option value="OPEN">OPEN</option>
                                        <option value="REVIEWED">REVIEWED</option>
                                        <option value="FLAGGED">FLAGGED</option>
                                        <option value="RESOLVED">RESOLVED</option>
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group className="mt-2">
                                    <Form.Label>Search</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Address / building id"
                                        value={adminModerationSearchInput}
                                        onChange={(e) => setAdminModerationSearchInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                setAdminModerationSearch(adminModerationSearchInput.trim());
                                            }
                                        }}
                                        disabled={adminModerationLoading}
                                    />
                                </Form.Group>

                                <div className="d-flex gap-2 mt-2">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => setAdminModerationSearch(adminModerationSearchInput.trim())}
                                        disabled={adminModerationLoading}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => {
                                            setAdminModerationSearchInput('');
                                            setAdminModerationSearch('');
                                        }}
                                        disabled={adminModerationLoading}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => fetchAdminModerationInbox({ page: 1, append: false })}
                                        disabled={adminModerationLoading}
                                    >
                                        Refresh
                                    </Button>
                                </div>
                            </Form>

                            {adminModerationLoading && adminModerationConversations.length === 0 ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                </div>
                            ) : adminModerationConversations.length === 0 ? (
                                <div className="text-muted p-3">No conversations found.</div>
                            ) : (
                                <>
                                    {adminModerationLoading && (
                                        <div className="text-center pb-2">
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    )}
                                    <ListGroup>
                                        {adminModerationConversations.map((c) => {
                                            const title = c.buildingAddress || c.buildingName || c.buildingId || 'Conversation';
                                            const timeLabel = c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : '';
                                            return (
                                                <ListGroup.Item
                                                    key={c.conversationId}
                                                    action
                                                    active={c.conversationId === adminModerationSelectedConversationId}
                                                    onClick={() => {
                                                        setAdminModerationSelectedConversationId(c.conversationId);
                                                        setAdminModerationSuccess(null);
                                                        setAdminModerationError(null);
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="fw-bold">{title}</div>
                                                        <Badge variant={getAdminModerationStatusVariant(c.moderationStatus)}>
                                                            {c.moderationStatus}
                                                        </Badge>
                                                    </div>
                                                    <div className="small text-muted">{adminParticipantsLabel(c.participants)}</div>
                                                    {c.lastMessagePreview && <div className="small mt-1">{c.lastMessagePreview}</div>}
                                                    {timeLabel && <div className="small text-muted mt-1">{timeLabel}</div>}
                                                    {c.isEnded && (
                                                        <div className="mt-2">
                                                            <Badge variant="secondary">Ended</Badge>
                                                        </div>
                                                    )}
                                                </ListGroup.Item>
                                            );
                                        })}
                                    </ListGroup>
                                </>
                            )}

                            {adminModerationPagination?.has_more && (
                                <div className="text-center mt-3">
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={handleAdminModerationLoadMore}
                                        disabled={adminModerationLoading}
                                    >
                                        Load more
                                    </Button>
                                </div>
                            )}
                        </Col>
                        <Col md={8} style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                            {!adminModerationSelectedConversationId ? (
                                <div className="text-muted">Select a conversation to review.</div>
                            ) : adminModerationDetailLoading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                </div>
                            ) : adminModerationDetail ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <div className="h5 mb-1">
                                                {adminModerationDetail.buildingAddress ||
                                                    adminModerationDetail.buildingName ||
                                                    adminModerationDetail.buildingId ||
                                                    'Conversation'}
                                            </div>
                                            <div className="small text-muted">
                                                {adminParticipantsLabel(adminModerationDetail.participants)}
                                            </div>
                                            <div className="small text-muted mt-1">
                                                Sell intent:{' '}
                                                {adminModerationDetail.listingContext?.offMarketSellWillingnessId || '—'} | Buy intent:{' '}
                                                {adminModerationDetail.listingContext?.offMarketBuyWillingnessId || '—'}
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <Badge
                                                variant={getAdminModerationStatusVariant(String(adminModerationDetail.moderationCase?.status || 'OPEN'))}
                                            >
                                                {String(adminModerationDetail.moderationCase?.status || 'OPEN')}
                                            </Badge>
                                            {adminModerationDetail.moderationCase?.lastModerationActionAt && (
                                                <div className="small text-muted mt-1">
                                                    Last action:{' '}
                                                    {new Date(adminModerationDetail.moderationCase.lastModerationActionAt).toLocaleString()}
                                                </div>
                                            )}
                                            {adminModerationDetail.isEnded && (
                                                <div className="small text-muted mt-1">Conversation ended</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 mb-3 flex-wrap">
                                        <Button
                                            size="sm"
                                            variant="outline-success"
                                            onClick={handleAdminMarkReviewed}
                                            disabled={adminModerationDetailLoading}
                                        >
                                            Mark reviewed
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={openFlagReasonModal}
                                            disabled={adminModerationDetailLoading}
                                        >
                                            Flag…
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={handleAdminResolve}
                                            disabled={adminModerationDetailLoading}
                                        >
                                            Resolve
                                        </Button>
                                    </div>

                                    <div className="mb-4">
                                        <div className="fw-bold mb-2">Messages</div>
                                        {adminModerationDetail.messages.length === 0 ? (
                                            <div className="text-muted">No messages.</div>
                                        ) : (
                                            <div>
                                                {adminModerationDetail.messages.map((m) => {
                                                    const ts = m.timestamp ? new Date(m.timestamp).toLocaleString() : '';
                                                    return (
                                                        <div key={m.messageId} className="p-2 mb-2 border rounded">
                                                            <div className="d-flex justify-content-between">
                                                                <div className="fw-bold">{m.senderEmail || m.senderId || 'Unknown sender'}</div>
                                                                <div className="small text-muted">{ts}</div>
                                                            </div>

                                                            {m.moderation?.hidden && (
                                                                <div className="mt-1">
                                                                    <Badge variant="warning" className="me-2">
                                                                        Hidden
                                                                    </Badge>
                                                                    {m.moderation.reason && (
                                                                        <span className="small text-muted">Reason: {m.moderation.reason}</span>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                                                {m.text ? m.text : <span className="text-muted">(no text)</span>}
                                                            </div>

                                                            {m.attachments?.length > 0 && (
                                                                <div className="small text-muted mt-2">
                                                                    Attachments:{' '}
                                                                    {m.attachments
                                                                        .map((a) => a.fileName || a.type || 'attachment')
                                                                        .filter(Boolean)
                                                                        .join(', ')}
                                                                </div>
                                                            )}

                                                            <div className="mt-2">
                                                                {m.moderation?.hidden ? (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        onClick={() => handleAdminUnhideMessage(m.messageId)}
                                                                        disabled={adminModerationDetailLoading}
                                                                    >
                                                                        Unhide
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-danger"
                                                                        onClick={() => openHideReasonModal(m.messageId)}
                                                                        disabled={adminModerationDetailLoading}
                                                                    >
                                                                        Hide…
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <div className="fw-bold mb-2">Audit log</div>
                                        {adminModerationDetail.actionLogs.length === 0 ? (
                                            <div className="text-muted">No actions yet.</div>
                                        ) : (
                                            <ListGroup>
                                                {adminModerationDetail.actionLogs.slice(0, 50).map((log) => (
                                                    <ListGroup.Item key={log.id}>
                                                        <div className="d-flex justify-content-between">
                                                            <div className="fw-bold">{log.action}</div>
                                                            <div className="small text-muted">
                                                                {log.performedAt ? new Date(log.performedAt).toLocaleString() : ''}
                                                            </div>
                                                        </div>
                                                        {log.reason && <div className="small text-muted">Reason: {log.reason}</div>}
                                                        {log.messageId && <div className="small text-muted">Message: {log.messageId}</div>}
                                                        {log.performedByAdminUserId && (
                                                            <div className="small text-muted">Admin: {log.performedByAdminUserId}</div>
                                                        )}
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-muted">Failed to load conversation detail.</div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>

            <Modal
                show={!!reasonModal}
                onHide={() => {
                    setReasonModal(null);
                    setReasonText('');
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {reasonModal?.type === 'FLAG_CONVERSATION' ? 'Flag conversation' : 'Hide message'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder={
                                reasonModal?.type === 'FLAG_CONVERSATION'
                                    ? 'Why are you flagging this conversation?'
                                    : 'Why are you hiding this message?'
                            }
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            disabled={reasonSubmitting}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => {
                            setReasonModal(null);
                            setReasonText('');
                        }}
                        disabled={reasonSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={reasonModal?.type === 'FLAG_CONVERSATION' ? 'danger' : 'warning'}
                        onClick={handleReasonSubmit}
                        disabled={reasonSubmitting}
                    >
                        {reasonSubmitting ? <Spinner animation="border" size="sm" /> : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminModerationTool;
